'use client';

import { useSession } from '@/providers/enhanced-session';
import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '../constants';
import type { ChatInputUsageLimit } from '../props';
import { studioAIService } from '../services';

/**
 * Returns the org-trial usage cap to render under `ChatInput`, or null when
 * the user/org has no configured cap (unlimited, per-user-licensed, or no
 * client trial access). Reads from the live `getAccessStatus` query — the
 * single source of truth for mutating org-scoped counters.
 */
export function useOrgTrialUsageLimit(): ChatInputUsageLimit | null {
    const { data: session } = useSession();

    const isFreemium = session?.user?.isUnaffiliated ?? false;
    const hasUserLicense = session?.user?.hasStudioAIAccess ?? false;
    const hasClientStudioAIAccess =
        (session?.org?.hasClientStudioAIAccess ?? false) && !hasUserLicense && !isFreemium;

    const { data: accessStatus } = useQuery({
        queryKey: QUERY_KEYS.ACCESS_STATUS,
        queryFn: () => studioAIService.getAccessStatus(),
        enabled: isFreemium || hasClientStudioAIAccess,
        staleTime: 1000 * 60 * 5,
        retry: 1,
    });

    const live = accessStatus?.clientStudioAIAccess;
    if (!live || live.limitType == null || live.limitValue == null) return null;

    return {
        limitType: live.limitType,
        limit: live.limitValue,
        currentCount: live.currentCount ?? 0,
        hasReachedLimit: live.hasReachedLimit ?? false,
        daysUntilReset: live.daysUntilReset ?? null,
        limitMessage: live.limitMessage ?? null,
    };
}
